import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";

function CertificateUploadModal({ isOpen, onClose, onUpload, title }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const resetForm = () => {
    setSelectedFile(null);
    setPassphrase("");
    setError("");
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    resetForm();
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError("");
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext !== "pfx" && ext !== "p12") {
      setError(t("cert-mgmt.invalid-file-type", { defaultValue: "Please select a .pfx or .p12 file." }));
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError(t("cert-mgmt.file-required", { defaultValue: "Please select a certificate file." }));
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result;
          const base64Data = result.split(",")[1];
          resolve(base64Data);
        };
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsDataURL(selectedFile);
      });

      await onUpload(base64, passphrase);
      resetForm();
    } catch (err) {
      setError(err.message || t("cert-mgmt.upload-failed", { defaultValue: "Upload failed." }));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={title || t("cert-mgmt.upload-certificate", { defaultValue: "Upload Certificate" })}
      onClose={handleClose}
      closeIconOnly
      closeLabel={t("close", { defaultValue: "Close" })}
    >
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label
            htmlFor="cert-file-input"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
          >
            {t("cert-mgmt.certificate-file", { defaultValue: "Certificate file (.pfx / .p12)" })}
          </label>
          <input
            ref={fileInputRef}
            id="cert-file-input"
            type="file"
            accept=".pfx,.p12"
            onChange={handleFileChange}
            className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none file:mr-3 file:rounded-lg file:border-0 file:bg-app-brand/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-app-brand"
          />
        </div>

        <Input
          id="cert-passphrase"
          type="password"
          label={t("cert-mgmt.passphrase", { defaultValue: "Passphrase" })}
          value={passphrase}
          onChange={(e) => {
            setPassphrase(e.target.value);
            setError("");
          }}
          placeholder={t("cert-mgmt.passphrase-placeholder", { defaultValue: "Enter certificate passphrase" })}
        />

        {error && <p className="m-0 text-sm text-app-danger">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={handleClose} disabled={isUploading}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={handleSubmit} disabled={isUploading || !selectedFile}>
            {isUploading
              ? t("loading", { defaultValue: "Loading..." })
              : t("cert-mgmt.upload", { defaultValue: "Upload" })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default CertificateUploadModal;
